import React, { useState } from 'react';

const ProducerCompany = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: 'I need help with Producer Company registration.',
    website_hp: ''
  });

 const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  try {
    const response = await fetch("https://akiradvisory.in/send_mail.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      setFormStatus('success');
      // Reset form while keeping the default message
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: 'I need help with Producer Company registration.',
        website_hp: ''
      });
    } else {
      setFormStatus('error');
    }
  } catch (error) {
    setFormStatus('error');
  } finally {
    setIsSubmitting(false);
    setTimeout(() => setFormStatus(''), 5000);
  }
};

  return (
    <main>
      {/* Hero Section */}
      <section className="relative bg-blue-700 pt-16 pb-12">
        <div className="absolute inset-0 opacity-10 bg-cover bg-center"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:w-2/3">
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Producer Company Registration
            </h1>
            <p className="mt-4 text-xl text-blue-100">
              A hybrid structure for primary producers and farmers to collectively improve their economic well-being and income.
            </p>
            <div className="mt-8">
              <a href="#contact-section" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
                  <span className="mr-2">🌱</span> Start Your Cooperative Business
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Navigation */}
      <section className="bg-gray-50 border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex space-x-6 overflow-x-auto text-sm font-medium">
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#details">Overview & Benefits</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#procedure">Registration Process</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#documents">Documents Required</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#you-get">What You Get</a></li>
                <li><a className="py-3 inline-block border-b-2 border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-600 transition duration-150" href="#service-faqs">FAQs</a></li>
            </ul>
        </div>
      </section>

      {/* Overview Section */}
      <section id="details" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 border-b pb-2">
                Overview of Producer Company
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                A <strong>Producer Company</strong> is a legally recognized entity formed by <strong>farmers or agriculturists</strong> with the primary goal of improving their livelihoods, financial well-being, and income. It is a hybrid structure, combining the professional management of a Private Limited Company with the mutual benefits of a Cooperative Society.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                It is established under the amended Companies Act of 1956 and governed by Section 465 of the Companies Act, 2013, and Part IX A of the Companies Act, 1956 (with modifications). Its objectives must align with activities specified in Section 581B, primarily related to the production, marketing, sale, and export of its members' primary products.
              </p>

              <div className="mt-8">
                <h3 className="text-2xl font-bold text-blue-600 mb-4">
                  Key Requirements & Advantages
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Minimum Members:</strong> Requires a minimum of <strong>10 producers</strong> or two producer institutions.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Minimum Directors:</strong> Must have a minimum of <strong>5 directors</strong>.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Minimal Capital:</strong> Minimum paid-up capital of <strong>Rs. 1 Lakh</strong> and minimum authorized capital of <strong>Rs. 5 Lakhs</strong>.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Limited Liability:</strong> Protects members by limiting their liability to their share contribution.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 flex-shrink-0 text-green-500 mt-1">✅</span>
                    <div>
                        <strong className="font-semibold">Autonomy & Focus:</strong> Owned by primary producers, ensuring autonomy and focus on member benefit (no government or private equity stake).
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-1" id="contact-section">
              <div className="bg-gray-50 p-6 rounded-xl shadow-xl border-t-4 border-blue-600 sticky top-28">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Quick Enquiry
                </h3>
                
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-100 text-green-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">✅</span>
                    Thank you! Your enquiry has been submitted. We'll contact you shortly.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 bg-red-100 text-red-800 rounded-lg flex items-center mb-4">
                    <span className="mr-2">❌</span>
                    There was an error submitting your form. Please try again.
                  </div>
                )}

                <form className="space-y-4" onSubmit={handleFormSubmit}>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your Full Name" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required 
                  />
                  <textarea 
                    rows="3" 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="I need help with Producer Company registration." 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" 
                    required
                  ></textarea>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:bg-blue-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3 inline-block"></div>
                        Submitting...
                      </>
                    ) : (
                      'Request an Initial Consultation'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Procedure Section */}
      <section id="procedure" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Our Comprehensive Process
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              9 Steps for Producer Company Registration
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { number: "01", icon: "📋", title: "KYC Submission & Advance Payment", desc: "Initial consultation, upload of documents, and making the 50% advance payment." },
              { number: "02", icon: "🔒", title: "Procure DSC & DIN", desc: "Application and procurement of Digital Signature Certificate (DSC) and Director Identification Number (DIN) for all proposed directors." },
              { number: "03", icon: "🏢", title: "Company Name Approval (RUN)", desc: "Filing Form SPICe+ with the ROC for the reservation of the company name, ending with 'Producer Company.'" },
              { number: "04", icon: "📝", title: "Drafting MoA & AoA", desc: "Preparation of the Memorandum (MoA) and Articles (AoA) of Association drafts, outlining objectives and by-laws." },
              { number: "05", icon: "✍️", title: "Document Signing by Subscribers", desc: "The prepared MoA, AoA, and all other necessary documents are sent for the directors' signatures." },
              { number: "06", icon: "💳", title: "Make Balance Payment", desc: "Completion of the remaining 50% balance payment for the registration process." },
              { number: "07", icon: "📤", title: "Final Filing (SPICe+)", desc: "Submission of the complete incorporation application (Form SPICe+) with all required annexures and fees to the ROC." },
              { number: "08", icon: "🖋️", title: "Issuance of CoI, PAN, TAN", desc: "The ROC issues the Certificate of Incorporation (CoI), along with the company's PAN and TAN." },
              { number: "09", icon: "🏦", title: "Bank Account & Commencement Setup", desc: "Post-incorporation activities including guidance for opening a bank account to start operations." }
            ].map((step, index) => (
              <div key={index} className="p-6 bg-white rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow border-t-4 border-blue-500 h-full">
                <div className="procedure-step-content">
                  <div>
                    <span className="text-2xl font-extrabold text-blue-600 block mb-2">{step.number}</span>
                    <span className="text-2xl mx-auto mb-2 block">{step.icon}</span>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{step.title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm mt-3">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section id="documents" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Only Scanned Copies are required
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Mandatory Documents for Producer Company
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: "👥", title: "Producers' KYC (All Subscribers)", desc: "PAN Card, Aadhaar Card, Recent Photograph, Email ID, and Contact Number for all proposed subscribers/members." },
              { icon: "🆔", title: "Directors' Identity & Address Proof", desc: "Self-attested identity and address proof for all directors, required for DIN procurement." },
              { icon: "🏢", title: "Registered Office Proof", desc: "Registered Office Proof (Utility Bill & NOC if owned, or Lease Agreement & NOC if rented) to be filed in Form INC-22." },
              { icon: "📄", title: "MoA & AoA Drafts", desc: "The drafted Memorandum of Association (MoA) and Articles of Association (AoA)." },
              { icon: "⚖️", title: "Affidavits & Declarations", desc: "Affidavits from all subscribers declaring legal competency, and Power of Attorney in favor of the consultant." },
              { icon: "📋", title: "Forms INC-22 and DIR-12", desc: "Prepared forms for Registered Office details (INC-22) and Directors Appointment (DIR-12)." }
            ].map((doc, index) => (
              <div key={index} className="p-6 border rounded-xl shadow-md flex items-start space-x-4 h-full">
                <span className="text-2xl flex-shrink-0 mt-1">{doc.icon}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{doc.title}</h3>
                  <p className="text-gray-600 text-sm mt-1">{doc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section id="you-get" className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Your Deliverables
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              What Akir Advisory Delivers
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: "📋", title: "Certificate of Incorporation (CoI)", desc: "The official legal document issued by the ROC certifying the company's existence." },
              { icon: "📚", title: "MoA & AoA", desc: "The Memorandum and Articles of Association (Charter documents) of the company." },
              { icon: "🆔", title: "Company PAN and TAN", desc: "The Permanent Account Number and Tax Deduction Account Number for the company." },
              { icon: "🔐", title: "DSCs & DINs", desc: "The Digital Signature Certificates and Director Identification Numbers for the members/directors." }
            ].map((item, index) => (
              <div key={index} className="p-6 bg-white rounded-xl shadow-lg border-l-4 border-green-500 h-full flex flex-col">
                <span className="text-2xl mb-3 block">{item.icon}</span>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="service-faqs" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
              Common Questions
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Producer Company FAQs
            </p>
          </div>
          
          <div className="space-y-4 max-w-4xl mx-auto">
            <div className="border rounded-lg p-5 shadow-sm">
              <h4 className="font-semibold text-lg text-gray-900 mb-2">What are the minimum requirements for members and directors?</h4>
              <p className="text-gray-600">A Producer Company requires a minimum of <strong>10 producers</strong> or two producer institutions as members, and a minimum of <strong>5 directors</strong> on the board.</p>
            </div>
            <div className="border rounded-lg p-5 shadow-sm">
              <h4 className="font-semibold text-lg text-gray-900 mb-2">What is the minimum capital requirement?</h4>
              <p className="text-gray-600">The company must have a minimum paid-up share capital of <strong>Rs. 1,00,000 (One Lakh Rupees)</strong> and a minimum authorized capital of <strong>Rs. 5,00,000 (Five Lakh Rupees)</strong>.</p>
            </div>
            <div className="border rounded-lg p-5 shadow-sm">
              <h4 className="font-semibold text-lg text-gray-900 mb-2">What are the primary objectives or key activities of this company type?</h4>
              <p className="text-gray-600">Key activities include the production, harvesting, procurement, grading, pooling, handling, marketing, selling, and exporting of the primary produce of the members, and importing goods or services for their welfare.</p>
            </div>
            <div className="border rounded-lg p-5 shadow-sm">
              <h4 className="font-semibold text-lg text-gray-900 mb-2">Is there a limit on profit sharing (dividends)?</h4>
              <p className="text-gray-600">Yes, the dividend on the shares of a Producer Company is capped at <strong>20%</strong> of the annual profits.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-blue-700/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-white">
            Ready to Establish Your Producer Company?
          </h2>
          <p className="mt-4 text-xl text-blue-100 max-w-3xl mx-auto">
            Empower your cooperative business with the right legal structure and expert registration support.
          </p>
          <a href="#contact-section" className="mt-8 inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full shadow-lg text-blue-700 bg-white hover:bg-gray-100 transition duration-150 ease-in-out transform hover:scale-105">
            <span className="mr-2">📞</span> Consult Our Expert Today
          </a>
        </div>
      </section>
    </main>
  );
};

export default ProducerCompany;